<template>
  <va-modal v-model="isVisible" :close-button="true" :hide-default-actions="true" :mobile-fullscreen="true">
    <div class="text-center">
      <h3 class="va-h3 mb-2">Sincronização do WhatsApp Business App</h3>

      <p class="mb-3 text-secondary">Acompanhamento do progresso de importação de contatos e histórico de mensagens.</p>

      <va-alert v-if="!phoneDigits && !phoneNumberId" color="warning" class="mb-3">
        Número não identificado. Abra o processamento a partir do onboarding ou configure uma integração do WhatsApp.
      </va-alert>

      <div v-if="phoneDigits" class="mb-3 text-sm text-gray-500">
        Sincronizando: {{ phoneDigits }}
        <va-button preset="plain" icon="refresh" size="small" title="Atualizar status" @click="startPolling()" />
      </div>

      <div class="mb-3">
        <va-progress-circle v-if="showSpinner" indeterminate color="primary" size="large" />
        <va-badge v-else :color="statusBadgeColor" :text="statusLabel" />
      </div>

      <div class="progress-details">
        <p>Status: {{ statusLabel }}</p>
        <p v-if="status.contacts_synced != null">
          📇 Contatos: {{ status.contacts_synced
          }}<span v-if="status.contacts_total != null"> / {{ status.contacts_total }}</span>
        </p>
        <p v-if="status.messages_synced != null">💬 Mensagens: {{ status.messages_synced }}</p>
        <p v-if="status.phase != null">📊 Fase: {{ status.phase }}</p>
        <p>Progresso: {{ status.progress ?? 0 }}%</p>
      </div>

      <va-alert v-if="displayErrorMessage" color="danger" class="mt-3">
        {{ displayErrorMessage }}
      </va-alert>

      <div class="mt-3 flex items-center justify-center gap-2">
        <va-button
          color="primary"
          size="small"
          :disabled="!phoneDigits || contactsCooldownMs > 0 || triggering === 'contacts'"
          :loading="triggering === 'contacts'"
          @click="initiateSync('contacts')"
        >
          Sincronizar Contatos
        </va-button>
        <va-button
          color="secondary"
          size="small"
          :disabled="!phoneDigits || historyCooldownMs > 0 || triggering === 'history'"
          :loading="triggering === 'history'"
          @click="initiateSync('history')"
        >
          Sincronizar Histórico
        </va-button>
      </div>

      <div v-if="contactsCooldownMs > 0 || historyCooldownMs > 0" class="mt-2 text-secondary text-sm">
        <span v-if="contactsCooldownMs > 0">Contatos em cooldown: {{ formatMsToMinSec(contactsCooldownMs) }}</span>
        <span v-if="historyCooldownMs > 0"> • Histórico em cooldown: {{ formatMsToMinSec(historyCooldownMs) }}</span>
      </div>
    </div>
  </va-modal>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { EventBus } from '@/services/eventBus.js'
import SettingsService from '@/services/settings/SettingsService'
import { useToast } from 'vuestic-ui'

const isVisible = ref(false)
const phoneNumberId = ref<string | null>(null) // fallback legacy
const phoneDigits = ref<string | null>(null)
const intervalRef = ref<number | null>(null)
const errorMessage = ref<string | null>(null)
const triggering = ref<null | 'contacts' | 'history'>(null)

const status = ref<{
  progress?: number
  messages_synced?: number
  contacts_synced?: number
  contacts_total?: number
  phase?: string | number | null
  status?: string | null
  last_error?: string | null
}>({
  progress: 0,
  messages_synced: 0,
  contacts_synced: 0,
  contacts_total: undefined as any,
  phase: null,
  status: null,
  last_error: null,
})

const statusCode = computed(() => String(status.value.status || '').toLowerCase())
const completed = computed(() => statusCode.value === 'completed' || (status.value.progress ?? 0) >= 100)
const failed = computed(() => statusCode.value === 'failed')
const pendingIdentifiers = computed(() => statusCode.value === 'pending_identifiers')
const showSpinner = computed(() => !completed.value && !failed.value && !pendingIdentifiers.value)
const displayErrorMessage = computed(() => status.value.last_error || errorMessage.value)
const statusLabel = computed(() => {
  if (completed.value) return 'Concluído'
  if (failed.value) return 'Falhou'
  if (pendingIdentifiers.value) return 'Aguardando identificadores'

  switch (statusCode.value) {
    case 'scheduled':
      return 'Agendado'
    case 'running':
      return 'Em andamento'
    default:
      return 'Em andamento'
  }
})
const statusBadgeColor = computed(() => {
  if (completed.value) return 'success'
  if (failed.value) return 'danger'
  if (pendingIdentifiers.value) return 'warning'
  return 'info'
})

// Cooldown configurável (minutos) — default 15
const COOLDOWN_MINUTES = Number((import.meta as any).env?.VITE_COEX_SYNC_COOLDOWN_MINUTES || 15)
const cooldownMs = COOLDOWN_MINUTES * 60 * 1000

const now = () => Date.now()
const identifier = () => phoneDigits.value || phoneNumberId.value || ''
const keyFor = (type: 'contacts' | 'history', key: string) => `coex_sync_last_${type}_${key}`
const getLastTs = (type: 'contacts' | 'history', key: string) => {
  const v = localStorage.getItem(keyFor(type, key))
  const n = v ? Number(v) : 0
  return Number.isFinite(n) ? n : 0
}
const setLastTs = (type: 'contacts' | 'history', key: string) => localStorage.setItem(keyFor(type, key), String(now()))

const contactsCooldownMs = computed(() => {
  const key = identifier()
  if (!key) return 0
  const elapsed = now() - getLastTs('contacts', key)
  return Math.max(0, cooldownMs - elapsed)
})
const historyCooldownMs = computed(() => {
  const key = identifier()
  if (!key) return 0
  const elapsed = now() - getLastTs('history', key)
  return Math.max(0, cooldownMs - elapsed)
})

const { init: notify } = useToast()

async function pollStatusByPhone(phone: string) {
  try {
    console.log('[CoexistenceSync] Polling status for:', phone)
    const resp = await SettingsService.checkCoexistenceStatusByPhone(phone)
    console.log('[CoexistenceSync] Status response:', resp?.data)

    if (resp?.data) {
      status.value = { ...status.value, ...resp.data }
    }

    if (completed.value || failed.value || pendingIdentifiers.value) {
      stopPolling()
    }
  } catch (e: any) {
    console.error('[CoexistenceSync] Polling failed:', e)
    // Estrategia simples: manter tentando; opcionalmente mostrar erro breve
    if (!errorMessage.value) {
      errorMessage.value = 'Não foi possível atualizar o status no momento.'
      setTimeout(() => (errorMessage.value = null), 4000)
    }
  }
}

function stopPolling() {
  if (intervalRef.value) {
    clearInterval(intervalRef.value)
    intervalRef.value = null
  }
}

function startPolling(phoneArg?: string) {
  const phone = phoneArg || phoneDigits.value
  if (!phone) return
  stopPolling()
  pollStatusByPhone(phone)
  intervalRef.value = window.setInterval(() => pollStatusByPhone(phone), 4000)
}

function formatMsToMinSec(ms: number) {
  const total = Math.ceil(ms / 1000)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}m ${s.toString().padStart(2, '0')}s`
}

async function initiateSync(type: 'contacts' | 'history') {
  const phone = phoneDigits.value
  if (!phone) return
  // Respeitar cooldown local
  const remain = type === 'contacts' ? contactsCooldownMs.value : historyCooldownMs.value
  if (remain > 0) return

  try {
    triggering.value = type
    errorMessage.value = null
    // Reset status visualmente para indicar início
    status.value = {
      progress: 0,
      messages_synced: 0,
      contacts_synced: 0,
      contacts_total: undefined,
      phase: 'Iniciando...',
      status: 'scheduled',
      last_error: null,
    }

    await SettingsService.initiateCoexistenceSyncByPhone(phone, type)
    // Se backend aceitar (202), iniciar polling e gravar timestamp
    setLastTs(type, phone)
    startPolling(phone)
    notify({ message: `Sincronização (${type === 'contacts' ? 'contatos' : 'histórico'}) iniciada`, color: 'success' })
  } catch (e: any) {
    // Respeitar Retry-After quando presente
    const retryAfter = Number(e?.response?.headers?.['retry-after'])
    if (Number.isFinite(retryAfter) && retryAfter > 0) {
      const ms = retryAfter * 1000
      notify({ message: `Aguarde ${formatMsToMinSec(ms)} para tentar novamente.`, color: 'warning' })
    } else if (e?.response?.status === 429) {
      notify({ message: 'Muitas tentativas. Tente novamente mais tarde.', color: 'warning' })
    } else {
      notify({ message: 'Não foi possível iniciar a sincronização.', color: 'danger' })
    }
  } finally {
    triggering.value = null
  }
}

function normalizePhone(raw: string | null): string | null {
  if (!raw) return null
  const digits = (raw.match(/\d+/g) || []).join('')
  return digits || null
}

async function openWithPhone(payload?: { phone?: string | null; phone_number_id?: string | null } | null) {
  isVisible.value = true
  errorMessage.value = null
  // 1) phone from payload
  const fromPayload = normalizePhone(payload?.phone || null)
  if (fromPayload) {
    phoneDigits.value = fromPayload
    return
  }

  // 2) sessionStorage phone
  try {
    const raw = sessionStorage.getItem('wa_display_phone_number')
    const digits = normalizePhone(raw)
    if (digits) {
      phoneDigits.value = digits
      return
    }
  } catch (e) {
    void e
  }

  // 3) Fallback: try resolve via settings (display_phone_number)
  try {
    const resp = await SettingsService.getSettings()
    const list = resp?.data?.results || resp?.data || []
    const whatsappList: any[] = Array.isArray(list)
      ? list.filter((x: any) => x?.integration_type === 'whatsapp' && x?.is_active !== false)
      : []
    let w: any = whatsappList.find((x: any) => x?.filtered_config?.is_coexistence === true)
    if (!w) w = whatsappList.sort((a, b) => (Number(b?.id) || 0) - (Number(a?.id) || 0))[0] || null
    const raw = w?.filtered_config?.display_phone_number || null
    const digits = normalizePhone(raw)
    if (digits) {
      phoneDigits.value = digits
      return
    }
  } catch (e) {
    void e
  }
}

const onSyncOpen = (payload?: any) => {
  openWithPhone(payload || null)
}

const onSyncStarted = (payload: any) => {
  openWithPhone(payload || null)
}

watch(
  [isVisible, phoneDigits],
  ([visible, phone]) => {
    if (visible && phone) {
      startPolling(phone)
    } else {
      stopPolling()
    }
  },
  { immediate: true },
)

onMounted(() => {
  // Attempt to prefill phone digits from sessionStorage for immediate actionability
  try {
    const raw = sessionStorage.getItem('wa_display_phone_number')
    const digits = normalizePhone(raw)
    if (!phoneDigits.value && digits) {
      phoneDigits.value = digits
    }
  } catch (e) {
    // ignore
  }
  EventBus.on('coexistence-sync-open', onSyncOpen)
  EventBus.on('coexistence-sync-started', onSyncStarted)
})

onUnmounted(() => {
  EventBus.off('coexistence-sync-open', onSyncOpen)
  EventBus.off('coexistence-sync-started', onSyncStarted)
  stopPolling()
})
</script>

<style scoped>
.progress-details {
  text-align: left;
  padding: 1rem;
  background: var(--va-background-element);
  border-radius: 4px;
}
</style>
